// Messages — direct messages between users. Two-pane layout: thread list
// on the left, active conversation on the right. Live-updates via SSE.
//
// Route: /messages, or /messages?with=<userID> to open a specific thread.

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { timeAgo } from '../lib/format';

interface Thread {
    other_user_id:      string;
    other_display:      string;
    last_body:          string;
    last_sent_at:       string;
    last_sender_is_me:  boolean;
    unread_count:       number;
}
interface Msg {
    id:             string;
    sender_id:      string;
    recipient_id:   string;
    body:           string;
    sent_at:        string;
    read_at?:       string | null;
    sender_display: string;
    sender_is_me:   boolean;
}

export function Messages() {
    const api = useApi();
    const { authed, login, token } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();

    // From ?with=<userID> — sync with URL so back-button + refresh work.
    const initialWith = new URLSearchParams(loc.search).get('with') || '';
    const [threads, setThreads] = useState<Thread[]>([]);
    const [openWith, setOpenWith] = useState<string>(initialWith);
    const [messages, setMessages] = useState<Msg[]>([]);
    const [err,      setErr]      = useState('');

    const loadThreads = useCallback(async () => {
        if (!authed) return;
        try {
            const r = await api.get<{ threads: Thread[] }>('/api/messages/threads');
            setThreads(r.threads ?? []);
        } catch (e) { setErr((e as Error).message); }
    }, [api, authed]);

    const loadThread = useCallback(async (otherID: string) => {
        if (!authed || !otherID) return;
        try {
            const r = await api.get<{ messages: Msg[] }>('/api/messages/thread/' + otherID);
            setMessages(r.messages ?? []);
            // Mark read; the badge in threads should also drop.
            await api.post('/api/messages/thread/' + otherID + '/read');
            await loadThreads();
        } catch (e) { setErr((e as Error).message); }
    }, [api, authed, loadThreads]);

    useEffect(() => { loadThreads(); }, [loadThreads]);
    useEffect(() => { if (openWith) loadThread(openWith); }, [openWith, loadThread]);

    // Sync URL when the user picks a thread from the list — enables back-button.
    useEffect(() => {
        const currentWith = new URLSearchParams(loc.search).get('with') || '';
        if (openWith && openWith !== currentWith) {
            nav('/messages?with=' + openWith, { replace: false });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openWith]);

    // Live delivery via SSE.
    useEffect(() => {
        const t = token();
        if (!t) return;
        const es = new EventSource('/api/messages/stream?access_token=' + encodeURIComponent(t));
        es.addEventListener('message', (e: MessageEvent) => {
            try {
                const m = JSON.parse(e.data) as Msg;
                // If the incoming msg belongs to the open thread, append.
                if (openWith && (m.sender_id === openWith || m.recipient_id === openWith)) {
                    setMessages(prev => [...prev, m]);
                }
                loadThreads();
            } catch { /* ignore */ }
        });
        es.onerror = () => { /* browser auto-reconnects */ };
        return () => { es.close(); };
    }, [token, openWith, loadThreads]);

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to see your messages.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );

    return (
        <main>
            <div className="messages-wrap">
                {err && <p style={{ color: '#8b1919', padding: 12 }}>{err}</p>}
                <div className="messages-layout">
                    <aside className="thread-list">
                        <h2>Conversations</h2>
                        {threads.length === 0 ? (
                            <p className="empty" style={{ margin: 12 }}>
                                No messages yet. Find someone via search (press <kbd>/</kbd>) or click a name in a post byline.
                            </p>
                        ) : threads.map(t => (
                            <button
                                type="button"
                                key={t.other_user_id}
                                className={'thread-row' + (openWith === t.other_user_id ? ' is-active' : '') + (t.unread_count > 0 ? ' has-unread' : '')}
                                onClick={() => setOpenWith(t.other_user_id)}
                            >
                                <div className="thread-top">
                                    <span className="thread-name">{t.other_display}</span>
                                    <span className="thread-when">{timeAgo(t.last_sent_at)}</span>
                                </div>
                                <div className="thread-last">
                                    {t.last_sender_is_me && <span className="thread-me">You: </span>}
                                    {t.last_body}
                                </div>
                                {t.unread_count > 0 && <span className="thread-badge">{t.unread_count}</span>}
                            </button>
                        ))}
                    </aside>

                    <section className="conversation">
                        {openWith ? (
                            <Conversation otherID={openWith} messages={messages} onSent={() => { loadThread(openWith); }} />
                        ) : (
                            <div className="conversation-empty">
                                Pick a conversation on the left, or search for someone with <kbd>/</kbd>.
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </main>
    );
}

function Conversation({ otherID, messages, onSent }: { otherID: string; messages: Msg[]; onSent: () => void }) {
    const api = useApi();
    const [body,    setBody]    = useState('');
    const [sending, setSending] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);

    // Scroll to bottom on new messages.
    useEffect(() => {
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages.length]);

    async function onSubmit(e: FormEvent) {
        e.preventDefault();
        const b = body.trim();
        if (!b) return;
        setSending(true);
        try {
            await api.post('/api/messages', { recipient_id: otherID, body: b });
            setBody('');
            onSent();
        } catch { /* let the user retry */ }
        setSending(false);
    }

    return (
        <div className="conversation-inner">
            <div className="conversation-list" ref={listRef}>
                {messages.length === 0 && (
                    <p style={{ textAlign: 'center', color: '#999', fontStyle: 'italic', padding: '40px 20px', fontFamily: "'Newsreader',serif" }}>
                        No messages yet. Say hi.
                    </p>
                )}
                {messages.map(m => (
                    <div className={'msg-row' + (m.sender_is_me ? ' me' : ' them')} key={m.id}>
                        <div className="msg-bubble">
                            <div className="msg-body">{m.body}</div>
                            <div className="msg-meta">{timeAgo(m.sent_at)}</div>
                        </div>
                    </div>
                ))}
            </div>
            <form className="conversation-composer" onSubmit={onSubmit}>
                <input
                    type="text"
                    placeholder="Type a message…"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    maxLength={4000}
                />
                <button type="submit" className="primary" disabled={sending || !body.trim()}>Send</button>
            </form>
        </div>
    );
}
